import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Search, IndianRupee, Phone, Calendar, Filter,
  Building2, RefreshCw, AlertCircle, Loader2, FileText, X, 
  CheckCircle, Clock, Wrench, Package
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MobilePendingPayments = () => {
  const [pendingSales, setPendingSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  
  // Filter states
  const [salesTypeFilter, setSalesTypeFilter] = useState('all'); // all, service, product
  const [billTypeFilter, setBillTypeFilter] = useState('all'); // all, on_bill, off_bill
  const [billStatusFilter, setBillStatusFilter] = useState('all'); // all, generated, pending

  const getToken = () => {
    return localStorage.getItem('suntech_mobile_token') || localStorage.getItem('token');
  };

  const fetchPendingSales = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/sales/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPendingSales(data);
      } else {
        toast.error('Failed to fetch pending payments');
      }
    } catch (error) {
      console.error('Error fetching pending payments:', error);
      toast.error('Failed to load pending payments');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingSales();
  }, [fetchPendingSales]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchPendingSales();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  // Filter sales based on all criteria
  const filteredSales = pendingSales.filter(sale => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      sale.sale_number?.toLowerCase().includes(searchLower) ||
      sale.customer_name?.toLowerCase().includes(searchLower) ||
      sale.customer_mobile?.includes(searchTerm) ||
      sale.customer?.company_name?.toLowerCase().includes(searchLower) ||
      sale.customer?.mobile?.includes(searchTerm);
    
    // Sales type filter
    const matchesSalesType = salesTypeFilter === 'all' ||
      (salesTypeFilter === 'service' && sale.is_service_invoice) ||
      (salesTypeFilter === 'product' && !sale.is_service_invoice);
    
    // Bill type filter
    const matchesBillType = billTypeFilter === 'all' ||
      (billTypeFilter === 'off_bill' && sale.is_off_bill) ||
      (billTypeFilter === 'on_bill' && !sale.is_off_bill);
    
    // Bill status filter
    const hasBillGenerated = !!sale.sale_number;
    const matchesBillStatus = billStatusFilter === 'all' ||
      (billStatusFilter === 'generated' && hasBillGenerated) ||
      (billStatusFilter === 'pending' && !hasBillGenerated);

    return matchesSearch && matchesSalesType && matchesBillType && matchesBillStatus;
  });

  // Calculate total pending amount
  const totalPending = filteredSales.reduce((sum, sale) => {
    return sum + (sale.net_amount - (sale.amount_paid || 0));
  }, 0);

  const hasActiveFilters = salesTypeFilter !== 'all' || billTypeFilter !== 'all' || billStatusFilter !== 'all';

  const clearFilters = () => {
    setSalesTypeFilter('all');
    setBillTypeFilter('all');
    setBillStatusFilter('all');
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      {/* Header Stats */}
      <Card className="bg-gradient-to-r from-green-500 to-emerald-600 text-white border-0">
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-green-100 text-sm">Total Pending</p>
              <p className="text-2xl font-bold">{formatCurrency(totalPending)}</p>
              <p className="text-green-100 text-xs mt-1">{filteredSales.length} pending payments</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={() => setFilterDialogOpen(true)}
              >
                <Filter className={`w-5 h-5 ${hasActiveFilters ? 'text-yellow-300' : ''}`} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-white hover:bg-white/20"
                onClick={handleRefresh}
                disabled={refreshing}
              >
                <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search by invoice, customer, phone..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
        {searchTerm && (
          <button 
            onClick={() => setSearchTerm('')}
            className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-gray-500">Filters:</span>
          {salesTypeFilter !== 'all' && (
            <Badge variant="outline" className="bg-orange-50 text-orange-700 flex items-center gap-1">
              {salesTypeFilter === 'service' ? 'Service' : 'Product'}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setSalesTypeFilter('all')} />
            </Badge>
          )}
          {billTypeFilter !== 'all' && (
            <Badge variant="outline" className="bg-blue-50 text-blue-700 flex items-center gap-1">
              {billTypeFilter === 'on_bill' ? 'On Bill' : 'Off Bill'}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setBillTypeFilter('all')} />
            </Badge>
          )}
          {billStatusFilter !== 'all' && (
            <Badge variant="outline" className="bg-purple-50 text-purple-700 flex items-center gap-1">
              Bill: {billStatusFilter === 'generated' ? 'Generated' : 'Pending'}
              <X className="w-3 h-3 cursor-pointer" onClick={() => setBillStatusFilter('all')} />
            </Badge>
          )}
          <Button variant="link" size="sm" className="text-red-500 p-0 h-auto" onClick={clearFilters}>
            Clear All
          </Button>
        </div>
      )}

      {/* Pending Payments List */}
      {filteredSales.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <IndianRupee className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="text-slate-500">No pending payments found</p>
            {hasActiveFilters && (
              <Button variant="link" onClick={clearFilters} className="mt-2">
                Clear filters to see all
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredSales.map((sale) => {
            const pendingAmount = sale.net_amount - (sale.amount_paid || 0);
            const overdue = isOverdue(sale.due_date);
            const hasBill = !!sale.sale_number;
            
            return (
              <Card 
                key={sale.id} 
                className={`border-l-4 ${overdue ? 'border-l-red-500 bg-red-50/50' : 'border-l-green-500'}`}
              >
                <CardContent className="p-4">
                  {/* Invoice Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-slate-800">
                        {sale.sale_number || 'Bill Pending'}
                      </p>
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {/* Sales Type Badge */}
                        {sale.is_service_invoice ? (
                          <Badge className="bg-orange-100 text-orange-700 text-xs">
                            <Wrench className="w-3 h-3 mr-1" />
                            Service
                          </Badge>
                        ) : (
                          <Badge className="bg-teal-100 text-teal-700 text-xs">
                            <Package className="w-3 h-3 mr-1" />
                            Product
                          </Badge>
                        )}
                        
                        {/* Bill Type Badge */}
                        {sale.is_off_bill ? (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">Off Bill</Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">
                            {sale.sale_type === 'Invoice' ? 'GST' : 'Cash'}
                          </Badge>
                        )}
                        
                        {/* Bill Status Badge */}
                        {hasBill ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Generated
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            Bill Pending
                          </Badge>
                        )}
                        
                        {/* Overdue Badge */}
                        {overdue && (
                          <Badge variant="destructive" className="text-xs">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Overdue
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">{formatCurrency(pendingAmount)}</p>
                      <p className="text-xs text-slate-500">pending</p>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{sale.customer_name || sale.customer?.company_name || 'N/A'}</span>
                    </div>
                    
                    {(sale.customer_mobile || sale.customer?.mobile) && (
                      <a 
                        href={`tel:${sale.customer_mobile || sale.customer?.mobile}`}
                        className="flex items-center gap-2 text-blue-600"
                      >
                        <Phone className="w-4 h-4" />
                        <span>{sale.customer_mobile || sale.customer?.mobile}</span>
                      </a>
                    )}

                    {/* Particulars/Description */}
                    {sale.sales_for && (
                      <div className="flex items-start gap-2 text-slate-500">
                        <FileText className="w-4 h-4 mt-0.5" />
                        <span className="text-xs line-clamp-2">{sale.sales_for}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <div className="flex items-center gap-2 text-slate-500">
                        <Calendar className="w-4 h-4" />
                        <span>Due: {formatDate(sale.due_date)}</span>
                      </div>
                      <div className="text-slate-500 text-xs">
                        Total: {formatCurrency(sale.net_amount)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onOpenChange={setFilterDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-green-600" />
              Filter Pending Payments
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Sales Type Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Sales Type</Label>
              <Select value={salesTypeFilter} onValueChange={setSalesTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="service">Service Sales</SelectItem>
                  <SelectItem value="product">Product Sales</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bill Type Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Bill Type</Label>
              <Select value={billTypeFilter} onValueChange={setBillTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Bills" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Bills</SelectItem>
                  <SelectItem value="on_bill">On Bill</SelectItem>
                  <SelectItem value="off_bill">Off Bill</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bill Status Filter */}
            <div className="space-y-2">
              <Label className="text-sm text-gray-600">Bill Status</Label>
              <Select value={billStatusFilter} onValueChange={setBillStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="generated">Bill Generated</SelectItem>
                  <SelectItem value="pending">Bill Pending</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" className="flex-1" onClick={clearFilters}>
                Clear All
              </Button>
              <Button className="flex-1 bg-green-600" onClick={() => setFilterDialogOpen(false)}>
                Apply Filters
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobilePendingPayments;
